import { useEffect, useMemo, useState } from "react"
import { FiSearch, FiUsers } from "react-icons/fi"
import { getAllStudentDetails } from "../../../services/operations/adminAPI"
import user_logo from "../../../assets/user.png"

export default function AllStudents() {
  const [students, setStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      const result = await getAllStudentDetails()
      if (result) setStudents(result)
      setLoading(false)
    }
    load()
  }, [])

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return students.filter(
      (s) =>
        `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q)
    )
  }, [students, search])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-richblack-5">All Students</h1>
          <p className="text-sm text-richblack-400">
            {loading ? "Loading…" : `${students.length} registered students`}
          </p>
        </div>
        <div className="relative">
          <FiSearch size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-richblack-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="form-style w-full pl-9 sm:w-72"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="h-36 animate-pulse rounded-xl bg-richblack-800" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-24">
          <FiUsers size={40} className="text-richblack-600" />
          <p className="text-richblack-400">
            {search ? "No students match your search" : "No students registered yet"}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s) => (
            <div
              key={s._id}
              className="flex gap-3 rounded-xl border border-richblack-700 bg-richblack-800 p-4 transition-colors hover:border-richblack-600"
            >
              <img
                src={s.image && s.image !== "/" ? s.image : user_logo}
                alt={s.firstName}
                className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
                onError={(e) => { e.currentTarget.src = user_logo }}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-richblack-5">
                  {s.firstName} {s.lastName}
                </p>
                <p className="truncate text-xs text-richblack-400">{s.email}</p>

                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    s.active
                      ? "bg-caribbeangreen-300/15 text-caribbeangreen-300"
                      : "bg-richblack-700 text-richblack-400"
                  }`}>
                    {s.active ? "Active" : "Inactive"}
                  </span>
                  <span className="rounded-full bg-richblack-700 px-2 py-0.5 text-[10px] text-richblack-300">
                    {s.courses?.length ?? 0} enrolled
                  </span>
                  {s.additionalDetails?.gender && (
                    <span className="rounded-full bg-richblack-700 px-2 py-0.5 text-[10px] text-richblack-400">
                      {s.additionalDetails.gender}
                    </span>
                  )}
                </div>

                {s.additionalDetails?.contactNumber && (
                  <p className="mt-1.5 text-[11px] text-richblack-500">
                    {s.additionalDetails.contactNumber}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer count */}
      {!loading && filtered.length > 0 && search && (
        <p className="text-center text-xs text-richblack-500">
          Showing {filtered.length} of {students.length} students
        </p>
      )}
    </div>
  )
}
